import { useEffect, useState, useRef } from "react";
import { useKeycloak } from "@react-keycloak/web";
import { markExplicitLogout } from "keycloak/keyCloakConfig";

const SESSION_EVENT_KEY = "sessionEvent";
const POPUP_ENDS_AT_KEY = "sessionPopupEndsAt";

const REDIRECT_URI = "https://contentstudio.gilead.com";

export const useSessionExpiration = () => {
  const { keycloak, initialized } = useKeycloak();

  const [showModal, setShowModal] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [isExpired, setIsExpired] = useState(false);

  const timeoutRef = useRef(null);
  const intervalRef = useRef(null);
  const isContinuingRef = useRef(false);
  const isRefreshingRef = useRef(false);

  // ── helpers ─────────────────────────────────

  const clearAllTimers = () => {
    clearTimeout(timeoutRef.current);
    clearInterval(intervalRef.current);
  };

  const broadcastEvent = (type) => {
    try {
      localStorage.setItem(
        SESSION_EVENT_KEY,
        JSON.stringify({ type, ts: Date.now() }),
      );
    } catch (_) {}
  };

  const getStoredEndsAt = () => {
    try {
      const v = localStorage.getItem(POPUP_ENDS_AT_KEY);
      if (!v) return null;
      const n = parseInt(v, 10);
      return isNaN(n) ? null : n;
    } catch (_) {
      return null;
    }
  };

  // ── countdown ───────────────────────────────

  const runCountdownTo = (endsAt) => {
    clearInterval(intervalRef.current);

    setCountdown(Math.max(0, Math.floor((endsAt - Date.now()) / 1000)));

    intervalRef.current = setInterval(() => {
      const remaining = Math.max(0, Math.floor((endsAt - Date.now()) / 1000));
      setCountdown(remaining);

      if (remaining <= 0) {
        clearInterval(intervalRef.current);
        localStorage.removeItem(POPUP_ENDS_AT_KEY);
        // Notify all other open tabs that the session has expired.
        broadcastEvent("expired");
        triggerExpired();
      }
    }, 1000);
  };

  // ── state transitions ───────────────────────

  const openPopupWithCountdown = (endsAt) => {
    localStorage.setItem(POPUP_ENDS_AT_KEY, String(endsAt));
    setIsExpired(false);
    setShowModal(true);
    runCountdownTo(endsAt);
  };

  const triggerExpired = () => {
    clearAllTimers();
    localStorage.removeItem(POPUP_ENDS_AT_KEY);
    // Persist the expired state so that if the user refreshes the page while
    // this popup is visible, the next load detects the flag and forces logout
    // before Keycloak can silently re-authenticate via the SSO session.
    localStorage.setItem("sessionExpired", "true");
    setIsExpired(true);
    setCountdown(0);
    setShowModal(true);
  };

  const scheduleLogoutTimer = () => {
    clearAllTimers();
    setIsExpired(false);
    setShowModal(false);

    if (!keycloak?.authenticated || !keycloak.tokenParsed) return;

    const exp = keycloak.tokenParsed.exp;
    const now = Math.floor(Date.now() / 1000);
    const timeLeft = exp - now;

    if (timeLeft <= 0) {
      triggerExpired();
      return;
    }

    if (timeLeft > 120) {
      timeoutRef.current = setTimeout(
        () => {
          openPopupWithCountdown(Date.now() + 120 * 1000);
        },
        (timeLeft - 120) * 1000,
      );
    } else {
      openPopupWithCountdown(Date.now() + timeLeft * 1000);
    }
  };

  // ── init ───────────────────────────────────

  useEffect(() => {
    if (!initialized) return;

    // ── Refresh-after-expiry guard ──────────────────────────────────────────
    // If the user refreshed the page while the expired popup was visible,
    // the sessionExpired flag is still in localStorage.  Force a full logout
    // here before Keycloak can silently re-authenticate via the SSO session.
    if (localStorage.getItem("sessionExpired")) {
      localStorage.removeItem("sessionExpired");
      keycloak.logout({ redirectUri: REDIRECT_URI }).catch(() => {
        window.location.href = REDIRECT_URI;
      });
      return;
    }

    const storedEndsAt = getStoredEndsAt();

    if (storedEndsAt) {
      if (storedEndsAt > Date.now()) {
        setShowModal(true);
        setIsExpired(false);
        runCountdownTo(storedEndsAt);
      } else {
        localStorage.removeItem(POPUP_ENDS_AT_KEY);
        triggerExpired();
      }
      return;
    }

    scheduleLogoutTimer();
    return () => clearAllTimers();
  }, [initialized]); // eslint-disable-line react-hooks/exhaustive-deps

  // ── logout ─────────────────────────────────

  const doLogout = async () => {
    clearAllTimers();
    setShowModal(false);

    localStorage.removeItem(POPUP_ENDS_AT_KEY);
    localStorage.removeItem("bearerToken");
    localStorage.removeItem("userToken");
    localStorage.removeItem("sessionExpired");

    // Tell every other open tab to close its popup and redirect immediately.
    // They must NOT call keycloak.logout() again – the server session is
    // ended by the tab that initiated the logout below.
    broadcastEvent("loggedOut");

    try {
      markExplicitLogout();
      await keycloak.logout({ redirectUri: REDIRECT_URI });
    } catch {
      window.location.href = REDIRECT_URI;
    }
  };

  const handleLogout = doLogout;
  const handleLogin = doLogout;

  const readExpFromRawToken = (token) => {
    try {
      const b64 = token.split(".")[1].replace(/-/g, "+").replace(/_/g, "/");
      return JSON.parse(atob(b64)).exp;
    } catch {
      return null;
    }
  };

  // ── CONTINUE FIXED ─────────────────────────

  const handleContinue = async () => {
    if (isContinuingRef.current) return;
    isContinuingRef.current = true;

    clearAllTimers();
    localStorage.removeItem(POPUP_ENDS_AT_KEY);
    localStorage.removeItem("sessionExpired"); // clear on successful continue

    setShowModal(false);
    setIsExpired(false);
    setCountdown(0);

    try {
      await keycloak.updateToken(-1);

      const freshExp =
        readExpFromRawToken(keycloak.token) || keycloak.tokenParsed?.exp || 0;

      const timeLeft = freshExp - Math.floor(Date.now() / 1000);

      if (timeLeft <= 0) {
        triggerExpired();
        broadcastEvent("expired");
        return;
      }

      // ✅ schedule WITHOUT using scheduleLogoutTimer
      if (timeLeft > 120) {
        timeoutRef.current = setTimeout(
          () => {
            openPopupWithCountdown(Date.now() + 120 * 1000);
          },
          (timeLeft - 120) * 1000,
        );
      } else {
        const grace = Math.min(5, Math.floor(timeLeft / 2));
        timeoutRef.current = setTimeout(() => {
          openPopupWithCountdown(Date.now() + (timeLeft - grace) * 1000);
        }, grace * 1000);
      }

      // 🔥 mark this tab as refresher
      isRefreshingRef.current = true;
      broadcastEvent("refreshed");

      setTimeout(() => {
        isRefreshingRef.current = false;
      }, 200);
    } catch {
      triggerExpired();
      broadcastEvent("expired");
    } finally {
      isContinuingRef.current = false;
    }
  };

  // ── cross-tab sync (FIXED) ─────────────────

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === POPUP_ENDS_AT_KEY) {
        if (e.newValue) {
          // Another tab started the countdown – mirror it here.
          const endsAt = parseInt(e.newValue, 10);
          if (endsAt > Date.now()) {
            setShowModal(true);
            setIsExpired(false);
            runCountdownTo(endsAt);
          }
        }
        // Do NOT close the modal when the key is removed:
        // the removal happens right before an "expired" or "refreshed"
        // broadcast arrives – those events own the modal state.
        return;
      }

      if (e.key === SESSION_EVENT_KEY && e.newValue) {
        const evt = JSON.parse(e.newValue);

        if (evt.type === "expired") {
          triggerExpired();
        }

        if (evt.type === "refreshed") {
          if (isRefreshingRef.current) return;
          clearAllTimers();
          localStorage.removeItem(POPUP_ENDS_AT_KEY);
          setShowModal(false);
          setIsExpired(false);
        }

        if (evt.type === "loggedOut") {
          // Another tab called logout and ended the server session.
          // Close any open popup and redirect — no second logout needed.
          clearAllTimers();
          setShowModal(false);
          window.location.href = REDIRECT_URI;
        }
      }
    };

    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, [keycloak]);

  return {
    showModal,
    countdown,
    isExpired,
    handleContinue,
    handleLogin,
    handleLogout,
  };
};
